import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { ApprovingInvoiceReportsSearchQuery } from '@/context/services/approving_invoice_reports';
import { CollapsableSearch } from '@/features/approving_invoice_reports/components/Search/components/CollapsableSearch';
import { APPROVING_SEARCH_RESPONSIVE_FLAG } from '@/features/approving_invoice_reports/components/Search/constants/featureFlags';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import { useNotification } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import styles from './Search.module.scss';
import { ActiveSearchValues } from './components/ActiveSearchValues';
import { ResponsiveSearchForm } from './components/ResponsiveSearchForm';
import { SearchForm } from './components/SearchForm';
import { FieldKey } from './hooks/useConfigActiveSearchValuesOrder';
import {
  useApPayeeInitialData,
  useConfigForm,
  useDeptInitialData,
  useOfficeMembersInitialData,
} from './hooks/useConfigForm';
import { SearchFormData, useSearchForm } from './hooks/useSearchForm';
import { useShowSearchValue } from './hooks/useShowSearchValue';

const cx = classnames.bind(styles);

interface SearchProps {
  defaultValues?: SearchFormData;
  onSearch: (data: ApprovingInvoiceReportsSearchQuery) => void;
  searchQuery?: ApprovingInvoiceReportsSearchQuery;
  isLoading?: boolean;
}

type ErrorFallbackProps = {
  error: Error | null;
};

const ErrorFallback: FC<ErrorFallbackProps> = ({ error }) => {
  const { t } = useTranslation();
  const notify = useNotification();
  useEffect(() => {
    notify.error({
      title: error?.message ?? t('system_error'),
      opts: {
        duration: 3000,
        style: {
          position: 'fixed',
        },
      },
    });
  }, [error, notify, t]);
  return <></>;
};
ErrorFallback.displayName = 'ErrorFallback';

export const InnerSearch = ({
  defaultValues,
  onSearch,
  searchQuery,
  isLoading,
}: SearchProps) => {
  const form = useForm<SearchFormData>({
    defaultValues,
    mode: 'all',
  });

  const payeeOptions = useApPayeeInitialData(
    defaultValues?.ap_payee_id ? [defaultValues.ap_payee_id] : []
  );

  const deptOptions = useDeptInitialData(defaultValues?.dept_ids ?? []);

  const reportDeptOptions = useDeptInitialData(
    defaultValues?.report_dept_ids ?? []
  );

  const memberOptions = useOfficeMembersInitialData(
    defaultValues?.office_member_ids ?? []
  );

  const {
    customProps,
    defaultOptions,
    reportDeptsSetting,
    deptsExisted,
    checkLengthValidationRules,
  } = useConfigForm();

  // NA-5172 — responsive search area: ON renders ResponsiveSearchForm (antd
  // Row/Col) and lets the wrapper follow the page width; OFF renders the
  // legacy SearchForm untouched.
  const isResponsive = useGetFlags(APPROVING_SEARCH_RESPONSIVE_FLAG);
  const FormComponent = isResponsive ? ResponsiveSearchForm : SearchForm;

  const appPayeeDefaultOptions = useMemo(() => {
    return [
      ...(defaultOptions.apPayeeDefaultOptions || []),
      ...(payeeOptions || []),
    ];
  }, [defaultOptions.apPayeeDefaultOptions, payeeOptions]);

  const reportDeptDefaultOptions = useMemo(() => {
    return [
      ...(defaultOptions.deptDefaultOptions || []),
      ...(reportDeptOptions || []),
    ];
  }, [defaultOptions.deptDefaultOptions, reportDeptOptions]);

  const { handleReset } = useSearchForm({
    form,
  });

  const onHandleSearch = useCallback(
    (data: SearchFormData) => {
      const { status, mf_file, ...rest } = data;
      onSearch({
        ...rest,
        status: status === '' ? undefined : status,
        mf_file: mf_file === '' ? undefined : mf_file,
        sort_fields: defaultValues?.sort_fields,
        sort_orders: defaultValues?.sort_orders,
      });
    },
    [onSearch, defaultValues]
  );

  /** Active search values to show */
  const excludeFields = useMemo(() => {
    const excludeFields: FieldKey[] = [];
    if (!reportDeptsSetting?.enabled || !deptsExisted?.enabled) {
      excludeFields.push('report_dept_ids');
    }
    if (!deptsExisted?.enabled) {
      excludeFields.push('dept_ids');
    }
    return excludeFields;
  }, [reportDeptsSetting, deptsExisted]);

  const { searchValuesShow } = useShowSearchValue({
    searchQuery,
    payeeOptions: appPayeeDefaultOptions,
    deptOptions,
    reportDeptOptions: reportDeptDefaultOptions,
    memberOptions,
    excludeFields,
  });
  return (
    <div className={cx('search-container')}>
      <div className={cx('header')}>
        <CollapsableSearch initialOpen fullWidth={isResponsive}>
          {() => {
            return (
              <FormComponent
                appPayeeDefaultOptions={appPayeeDefaultOptions}
                reportDeptDefaultOptions={reportDeptDefaultOptions}
                customProps={customProps}
                checkLengthValidationRules={checkLengthValidationRules}
                deptsExisted={deptsExisted}
                reportDeptsSetting={reportDeptsSetting}
                form={form}
                onSubmit={onHandleSearch}
                onReset={handleReset}
                deptOptions={deptOptions}
                memberOptions={memberOptions}
                isLoading={isLoading}
              />
            );
          }}
        </CollapsableSearch>
      </div>

      {/* Search Results Reflection List */}
      <ActiveSearchValues activeSearchValues={searchValuesShow} />
    </div>
  );
};

export const Search = ({
  defaultValues,
  onSearch,
  searchQuery,
  isLoading,
}: SearchProps) => {
  return (
    // make sure search container loading same height as search container
    <Suspense
      fallback={<SuspenseLoading className={cx('search-container-loading')} />}
    >
      <ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />}>
        <InnerSearch
          defaultValues={defaultValues}
          onSearch={onSearch}
          searchQuery={searchQuery}
          isLoading={isLoading}
        />
      </ErrorBoundary>
    </Suspense>
  );
};
Search.displayName = 'Search';
